import { getLlmUserIds } from '@/api/me';
import { currentUserKey } from '@/utils/session';

/**
 * The signed-in user's id from GET /llm/me/info (chat-runs-api.md §15), so
 * that a run is the user's own when its `user_id` matches, whichever device
 * started it. Until the id is known, or when the API does not send it, the
 * run store falls back to the runs this browser started.
 *
 * The id is remembered with the session key it was read for, so it never
 * applies to another user who signs in later in the same page.
 */

let known: { key: string; id: number } | null = null;
/** The read in flight, for the user it was started for. */
let loading: { key: string; promise: Promise<number | null> } | null = null;

/** The signed-in user's id, or null while unknown. */
export const currentUserId = (): number | null => {
  const key = currentUserKey();
  return known && key && known.key === key ? known.id : null;
};

/** Read the id once per signed-in user (again after a failure). */
export const loadCurrentUserId = (): Promise<number | null> => {
  const key = currentUserKey();
  if (!key) return Promise.resolve(null);
  if (known?.key === key) return Promise.resolve(known.id);
  // A read started for another user is not reused.
  if (loading?.key === key) return loading.promise;
  const promise = getLlmUserIds()
    .then(ids => {
      // Signed out or switched user meanwhile: the answer is not this user's.
      if (ids && currentUserKey() === key) known = { key, id: ids.id };
      return ids && currentUserKey() === key ? ids.id : null;
    })
    .finally(() => {
      if (loading?.promise === promise) loading = null;
    });
  loading = { key, promise };
  return promise;
};

/** Tests: forget the id. */
export const resetCurrentUserId = () => {
  known = null;
  loading = null;
};
